import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@repo/core-ui";
import { useChannelManager } from "./hooks/useChannelManager";
import { ChannelManagerHeader } from "./components/channel/ChannelManagerHeader";
import { ChannelManagerFilters } from "./components/channel/ChannelManagerFilters";
import { ChannelTable } from "./components/channel/ChannelTable";
import { ChannelProfileModal } from "./components/channel/ChannelProfileModal";
import { ChannelDetailsModal } from "./components/channel/ChannelDetailsModal";

const ChannelManager: React.FC = () => {
  const {
    channels,
    loading,
    searchQuery,
    setSearchQuery,
    status,
    setStatus,
    page,
    pageSize,
    totalCount,
    isModalOpen,
    isViewModalOpen,
    selectedChannel,
    isEditMode,
    isSaving,
    insertForm,
    setInsertForm,
    updateForm,
    setUpdateForm,
    handleOpenInsertModal,
    handleEdit,
    handleView,
    handleSave,
    handleCloseModal,
    handleNextPage,
    handlePrevPage,
  } = useChannelManager();

  const { startItem, endItem } = useMemo(() => {
    return {
      startItem: (page - 1) * pageSize + 1,
      endItem: Math.min(
        page * pageSize,
        totalCount > 0 ? totalCount : page * pageSize,
      ),
    };
  }, [page, pageSize, totalCount]);

  const isNextDisabled = useMemo(
    () =>
      channels.length < pageSize &&
      (totalCount === 0 || page * pageSize >= totalCount),
    [channels.length, pageSize, totalCount, page],
  );

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      {/* Header Section */}
      <ChannelManagerHeader onRegisterNew={handleOpenInsertModal} />

      {/* Filter/Search Bar */}
      <ChannelManagerFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        status={status}
        setStatus={setStatus}
      />

      {/* Channel List Table */}
      <ChannelTable
        loading={loading}
        channels={channels}
        onEdit={handleEdit}
        onView={handleView}
      />

      {/* Pagination Controls */}
      {!loading && channels.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-white dark:bg-[#111c44] border border-slate-100 dark:border-white/10 rounded-[28px] shadow-[0_10px_40px_rgba(0,0,0,0.02)]">
          <div className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
            Node{" "}
            <span className="text-slate-900 dark:text-slate-200">
              {startItem}
            </span>{" "}
            —{" "}
            <span className="text-slate-900 dark:text-slate-200">
              {endItem}
            </span>
            {totalCount > 0 && (
              <>
                {" "}
                of{" "}
                <span className="text-blue-600 dark:text-blue-400">
                  {totalCount}
                </span>{" "}
                units registered
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handlePrevPage}
              disabled={page === 1}
              className="h-10 px-4 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 border-none transition-all font-bold"
            >
              <ChevronLeft size={18} />
              <span className="ml-1 text-sm">Prev</span>
            </Button>

            <div className="h-10 px-4 bg-[#111c44] border border-white/10 text-white rounded-xl text-sm font-black flex items-center justify-center shadow-lg shadow-black/20">
              {page}
            </div>

            <Button
              variant="ghost"
              onClick={handleNextPage}
              disabled={isNextDisabled}
              className="h-10 px-4 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 border-none transition-all font-bold"
            >
              <span className="mr-1 text-sm">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Registration / Edit Modal */}
      <ChannelProfileModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isEditMode={isEditMode}
        isSaving={isSaving}
        handleSave={handleSave}
        insertForm={insertForm}
        setInsertForm={setInsertForm}
        updateForm={updateForm}
        setUpdateForm={setUpdateForm}
      />

      {/* Details View Modal */}
      <ChannelDetailsModal
        isOpen={isViewModalOpen}
        onClose={handleCloseModal}
        channel={selectedChannel}
      />
    </div>
  );
};

export default ChannelManager;
